/* BLOOM: TASK-005 — rotating announcement bar (sections/bloom-announcement-bar.liquid) */
if (!customElements.get('bloom-announcement-bar')) {
  class BloomAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slidesWrapper = this.querySelector('.bloom-announcement-bar__slides');
      this.prevButton = this.querySelector('.bloom-announcement-bar__arrow--prev');
      this.nextButton = this.querySelector('.bloom-announcement-bar__arrow--next');
      this.bar = this.closest('.bloom-announcement-bar');

      this.interval = Math.max(3, parseInt(this.dataset.interval, 10) || 5) * 1000;
      this.effect = this.dataset.effect === 'slide' ? 'slide' : 'fade';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.hovered = false;
      this.focused = false;
      this.editorPaused = false;

      this.applySchedule();
      if (this.slides.length === 0) return;

      this.current = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.slides.forEach((slide, index) => this.setSlideState(slide, index === this.current));

      if (this.slides.length < 2) return;

      // Arrows: when enabled in the editor, or always under reduced motion (no rotation then).
      if (this.dataset.arrows === 'true' || this.reducedMotion) {
        this.prevButton.hidden = false;
        this.nextButton.hidden = false;
      }

      this.onPrev = () => this.goTo(this.current - 1, -1, true);
      this.onNext = () => this.goTo(this.current + 1, 1, true);
      this.prevButton.addEventListener('click', this.onPrev);
      this.nextButton.addEventListener('click', this.onNext);

      this.onEnter = () => { this.hovered = true; this.updateTimer(); };
      this.onLeave = () => { this.hovered = false; this.updateTimer(); };
      this.onFocusIn = () => { this.focused = true; this.updateTimer(); };
      this.onFocusOut = (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.updateTimer();
      };
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      if (window.Shopify && Shopify.designMode) {
        this.onBlockSelect = (event) => {
          const index = this.slides.indexOf(event.target);
          if (index === -1) return;
          this.editorPaused = true;
          this.goTo(index, index >= this.current ? 1 : -1, false);
          this.updateTimer();
        };
        this.onBlockDeselect = () => {
          this.editorPaused = false;
          this.updateTimer();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.updateTimer();
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.onPrev) {
        this.prevButton.removeEventListener('click', this.onPrev);
        this.nextButton.removeEventListener('click', this.onNext);
        this.removeEventListener('mouseenter', this.onEnter);
        this.removeEventListener('mouseleave', this.onLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        this.onPrev = null;
      }
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    // Pages can be served from cache: re-check show from / until with the visitor's date.
    applySchedule() {
      const now = new Date();
      const pad = (value) => String(value).padStart(2, '0');
      const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      const allSlides = Array.from(this.querySelectorAll('.bloom-announcement-bar__slide'));

      allSlides.forEach((slide) => {
        const start = slide.dataset.start;
        const end = slide.dataset.end;
        slide.hidden = Boolean((start && today < start) || (end && today > end));
      });

      this.slides = allSlides.filter((slide) => !slide.hidden);
      if (this.slides.length > 0 && !this.slides.some((slide) => slide.classList.contains('is-active'))) {
        allSlides.forEach((slide) => slide.classList.remove('is-active'));
        this.slides[0].classList.add('is-active');
      }
      if (this.bar) this.bar.hidden = this.slides.length === 0;
    }

    setSlideState(slide, active) {
      slide.classList.toggle('is-active', active);
      if (active) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
      slide.querySelectorAll('a, button').forEach((element) => {
        if (active) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', '-1');
        }
      });
    }

    goTo(targetIndex, direction, manual) {
      const count = this.slides.length;
      const index = ((targetIndex % count) + count) % count;
      if (index === this.current) return;

      // Announce only manual changes; automatic rotation stays silent for screen readers.
      this.slidesWrapper.setAttribute('aria-live', manual ? 'polite' : 'off');

      const outgoing = this.slides[this.current];
      const incoming = this.slides[index];

      if (this.effect === 'slide' && !this.reducedMotion) {
        incoming.style.transition = 'none';
        incoming.style.transform = `translateX(${direction * 100}%)`;
        incoming.getBoundingClientRect();
        incoming.style.transition = '';
        incoming.style.transform = '';
        outgoing.style.transform = `translateX(${direction * -100}%)`;
        window.clearTimeout(this.cleanupTimer);
        this.cleanupTimer = window.setTimeout(() => {
          this.slides.forEach((slide) => {
            if (!slide.classList.contains('is-active')) {
              slide.style.transition = 'none';
              slide.style.transform = '';
              slide.getBoundingClientRect();
              slide.style.transition = '';
            }
          });
        }, 550);
      }

      this.setSlideState(outgoing, false);
      this.setSlideState(incoming, true);
      this.current = index;

      if (manual) this.restartTimer();
    }

    isPaused() {
      return this.editorPaused || (this.pauseOnHover && (this.hovered || this.focused));
    }

    updateTimer() {
      if (this.autoplay && !this.isPaused()) {
        if (!this.timer) this.startTimer();
      } else {
        this.stopTimer();
      }
    }

    startTimer() {
      this.stopTimer();
      this.timer = window.setInterval(() => this.goTo(this.current + 1, 1, false), this.interval);
    }

    stopTimer() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartTimer() {
      this.stopTimer();
      this.updateTimer();
    }
  }

  customElements.define('bloom-announcement-bar', BloomAnnouncementBar);
}
